import type { ReactNode } from 'react';

import type { SerializableNode } from '@/serializer';

import type { DreamRenderElementProps } from '../../types';
import type { GroupSubscriptionPaymentCollectionMethod } from '../groupSubscriptionsElement/groupSubscriptionTypes';

/**
 * Shapes for the admin Billing tab.
 *
 * snake_case to match the Swarm `GET /api/v2/group_subscriptions/:id/billing`
 * response — orchid/dream-components mostly pass the API result straight
 * through, so the wire shape IS the component shape. Same call we made for
 * `GroupSubscriptionData` (BEE-19587): an aspirational camelCase type drifts
 * from the backend the moment the real endpoint lands.
 */

export type BillingCadence = 'month' | 'quarter' | 'year' | 'one_time';

/**
 * Mirrors `GroupSubscription#billing_status` on the Rails side: enum states plus two computed
 * ones — `cancels_at_renewal` when `ends_at` is set, `pending_reduction` when a seat reduction
 * is scheduled. Both computed states mean the plan is still running; a terminal status wins.
 */
export type BillingStatus =
  | 'active'
  | 'past_due'
  | 'payment_failed'
  | 'canceled'
  | 'cancels_at_renewal'
  | 'pending_reduction';

/** No further payment will be taken — either the plan is over or it stops at the term end. */
export const ENDING_BILLING_STATUSES: ReadonlySet<BillingStatus> = new Set([
  'canceled',
  'payment_failed',
  'cancels_at_renewal',
]);

export type BillingPaymentMethod = {
  brand: string;
  last4: string;
};

/** A single payment's amount + date. `date` is an ISO 8601 datetime string. */
export type BillingPaymentSnapshot = {
  amount_cents: number;
  date: string;
};

export type BillingPlanData = {
  id: string;
  /**
   * Set by the orchid loader, not the billing endpoint — the endpoint is
   * `group_subscriptions/:id/billing`, so this is always 'group' today. It
   * stays in the shape because the billing tab will aggregate individual
   * subscriptions later, and the card already branches on it.
   */
  plan_type: 'group' | 'individual';
  /**
   * Set by the orchid loader. 'admin' renders the full payment card; 'member'
   * (future) renders a header + feedback alert with no payment data.
   */
  viewer_role: 'admin' | 'member';
  group_name: string;
  tier_name: string;
  status: BillingStatus;
  seat_count: number;
  /** null when no seat-count change is scheduled. */
  scheduled_seat_count: number | null;
  price_per_seat_cents: number;
  total_cost_cents: number;
  /** Stripe currency code, set during checkout — see `payment_processor.rb`. */
  currency: string;
  /** Stripe `recurring.interval`, set during checkout — see `payment_processor.rb`. */
  interval: BillingCadence;
  /** ISO 8601 datetime — Rails `current_period_end`. Drives the "next payment" snapshot.
   *  null for `one_time` plans, which never renew. */
  renews_at: string | null;
  /** ISO 8601 datetime — Rails `term_starts_at`. Anchors the "Paid on" date on the
   *  one-time "Amount paid" snapshot. null for on-platform groups (no deal). */
  term_starts_at: string | null;
  /** ISO 8601 datetime; null unless the plan is set to end. */
  ends_at: string | null;
  payment_collection_method: GroupSubscriptionPaymentCollectionMethod;
  purchaser_email: string;
  /** The deal's billing contact — who members/admins reach out to for off-platform
   *  billing questions. Distinct from `purchaser_email` (the group admin). null for
   *  non-custom-deal groups, which have no `group_subscription_deal`. */
  publication_contact_email: string | null;
  /** Stripe Customer Portal session URL; null while the session is still being created. */
  stripe_customer_portal_url: string | null;
  /** Card brand + last4 from the purchaser's Stripe payment method. null when the
   *  customer has no card on file (comp / manual invoicing / off-platform). */
  payment_method: BillingPaymentMethod | null;
  /** The most recent paid invoice's amount + date. null when the plan hasn't been
   *  billed yet (e.g. off-platform, which has no Stripe invoice). */
  last_payment: BillingPaymentSnapshot | null;
};

export type TAdminBillingAttributes = {
  id?: string;
  data: {
    plans: BillingPlanData[];
  };
};

export type TAdminBillingElement = {
  type: 'adminBilling';
  attrs: TAdminBillingAttributes;
  content?: SerializableNode[];
};

/**
 * Host-provided translator, shaped like i18next's `t`. The element calls this
 * per plan card so translation AND interpolation stay on the host side — the
 * billing strings carry per-plan values (price, cadence, renewal date) that a
 * flat `translations` record can't hold, and the i18n templates need to keep
 * their full phrasing for word order to localize correctly.
 */
export type AdminBillingTranslate = (key: string, vars?: Record<string, string>) => string;

/**
 * `children` is optional here — the element is leaf-like (renders cards from
 * `attrs.data.plans`) and has no inline content slot, so callers like
 * orchid-v2 don't have to pass `null` just to satisfy the shared shape.
 */
export type AdminBillingElementProps = {
  isLive?: boolean;
  isLoading?: boolean;
  translate?: AdminBillingTranslate;
} & Omit<DreamRenderElementProps<TAdminBillingElement>, 'children'> & {
    children?: ReactNode;
  };
